import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, CheckCircle2, X, Award, ExternalLink } from "lucide-react";
import { BELTS } from "../../data/belts";

export const BeltModal = ({ activeId, onClose }) => {
  const belt = BELTS.find((b) => b.id === activeId);

  // Lock body scroll while open, and close on Escape.
  useEffect(() => {
    if (!belt) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [belt, onClose]);

  return (
    <AnimatePresence>
      {belt && (
        <motion.div
          key="modal-root"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
          data-testid="belt-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="absolute inset-0 bg-[#121212]/45 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="belt-modal-backdrop"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="belt-modal-close"
              className="absolute top-3.5 right-3.5 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#52525B] hover:text-[#121212] hover:bg-zinc-100 transition-colors border border-zinc-200"
            >
              <X size={16} strokeWidth={2.4} />
            </button>

            <div className="p-6 sm:p-7">
              {/* State badge */}
              <div className="flex items-center gap-2 pr-10">
                <div className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#52525B]">
                  Step {belt.id} of {BELTS.length}
                </div>
                {belt.locked ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#121212] px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-white"
                    data-testid="belt-locked-badge"
                  >
                    <Lock size={11} /> Locked
                  </span>
                ) : belt.levelComplete ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#F15D4E] px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-white"
                    data-testid="belt-level-badge"
                  >
                    <CheckCircle2 size={11} /> Level {belt.levelComplete} complete
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-[#52525B]">
                    In training
                  </span>
                )}
              </div>

              {/* Belt visual + heading */}
              <div className="mt-5 flex items-center gap-4">
                <div className="relative flex-shrink-0">
                  <div
                    className="h-12 w-24 rounded-md shadow-md"
                    style={{ background: belt.color, border: `2px solid ${belt.ring}` }}
                  >
                    <div
                      className="mx-3 my-3 h-1.5 rounded-sm"
                      style={{ background: belt.ring, opacity: 0.5 }}
                    />
                  </div>
                  <div
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-2.5 w-9 rounded-sm"
                    style={{ background: belt.ring }}
                  />
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-[0.18em] text-[#52525B]">
                    {belt.weeks}
                  </div>
                  <h3
                    data-testid="belt-name"
                    className="text-2xl font-medium tracking-tight text-[#121212] mt-0.5"
                  >
                    {belt.name}
                  </h3>
                </div>
              </div>

              {/* Quote */}
              <blockquote
                data-testid="belt-quote"
                className="mt-5 border-l-4 border-[#21b766] pl-3.5 py-0.5 italic text-[15px] text-[#121212]"
              >
                {`"${belt.quote}"`}
              </blockquote>

              {/* Summary */}
              <p
                data-testid="belt-summary"
                className="mt-3 text-[14px] leading-relaxed text-[#52525B]"
              >
                {belt.summary}
              </p>

              {/* Main steps achieved */}
              <div className="mt-5">
                <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.22em] text-[#21b766]">
                  <Award size={13} />
                  Main steps achieved
                </div>
                <ul className="mt-3 space-y-2" data-testid="belt-achievements">
                  {belt.achievements.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13px] text-[#121212]">
                      <span
                        className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full"
                        style={{
                          background: belt.locked
                            ? "#A1A1AA"
                            : belt.color === "#FFFFFF"
                              ? "#21b766"
                              : belt.color,
                        }}
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Optional waiting-list link (Level 4) */}
              {belt.waitlistUrl && (
                <a
                  href={belt.waitlistUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="belt-waitlist-link"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#F15D4E] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#d95346] transition-colors"
                >
                  {belt.waitlistLabel || "Join the waiting list"}
                  <ExternalLink size={14} strokeWidth={2.2} />
                </a>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
